import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface CoinbasePaymentLink {
  id: string;
  url: string;
  status: string;
  amount: string;
  currency: string;
  description: string;
  network: string;
  address: string;
  expiresAt: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

interface ActionTestResponse {
  data?: {
    result?: {
      success?: boolean;
      paymentLinks?: CoinbasePaymentLink[];
      error?: {
        code: string;
        message: string;
      };
    };
  };
  error?: {
    message: string;
  };
}

const VALID_EVENT_STATUSES: Record<string, string> = {
  completed: "success",
  failed: "failed",
  expired: "expired",
};

function mapCoinbaseStatus(status: string): string | null {
  return VALID_EVENT_STATUSES[status.toLowerCase()] ?? null;
}

/**
 * Fetch recent payment links from Coinbase Business via the
 * coinbase-business-list-payment-links action.
 *
 * Used as fallback data for the coinbase-payment-received trigger
 * in both draft and live modes.
 *
 * Requires: Coinbase Business connector with API key credentials.
 *
 * Note: uses /v1/actions/.../test instead of /v1/action-proxy/.../query
 * because the action-proxy endpoint does not forward connector credentials.
 */
export const coinbasePaymentFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { connectorId } = ctx;

  if (!connectorId) {
    throw new Error("No Coinbase Business connector linked. Add a Coinbase Business connector to test this trigger.");
  }

  const response = await authenticatedFetch<ActionTestResponse>(
    `${V2_ENDPOINT}/v1/actions/coinbase-business-list-payment-links/test`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        inputs: { pageSize: 10 },
        connector: { id: connectorId, type: "coinbase-business" },
      }),
      signal: AbortSignal.timeout(30_000),
    },
  );

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Failed to fetch payment links from Coinbase (HTTP ${response.status}): ${text}`);
  }

  const json = await response.json();

  const result = json.data?.result;
  if (result?.success === false) {
    const msg = result.error?.message || JSON.stringify(result.error ?? result);
    throw new Error(`Coinbase action failed: ${msg}`);
  }

  if (json.error?.message) {
    throw new Error(json.error.message);
  }

  const links: CoinbasePaymentLink[] = result?.paymentLinks ?? [];
  if (links.length === 0) return [];

  return links.flatMap(link => {
    const eventSuffix = mapCoinbaseStatus(link.status);
    if (!eventSuffix) return [];
    return [
      {
        triggeredAt: link.updatedAt || link.createdAt,
        eventType: `payment_link.payment.${eventSuffix}`,
        paymentLink: {
          id: link.id,
          url: link.url,
          status: link.status,
          amount: link.amount,
          currency: link.currency,
          description: link.description,
          network: link.network,
          address: link.address,
          expiresAt: link.expiresAt,
          metadata: link.metadata,
          createdAt: link.createdAt,
          updatedAt: link.updatedAt,
        },
        // Display metadata (stripped before execution)
        _amount: `${link.amount} ${link.currency}`,
        _status: link.status.toLowerCase(),
        _description: link.description || "No description",
        _network: link.network || "base",
        _createdAt: link.createdAt,
      },
    ];
  });
};
